"use client"

import { useEffect, useState } from "react"
import api from "../utils/api"
import RoleMenu from "../components/RoleMenu"
import GroupSearchPanel from "../components/groups/GroupSearchPanel"
import Icon from "../components/Icon"
// eslint-disable-next-line no-unused-vars
import BankCombobox from "../components/BankCombobox"
import ProfileModal from "../components/ProfileModal"
import ConfirmModal from "../components/ConfirmModal"
import Skeleton, { SkeletonList } from "../components/Skeleton"
import DashboardNotifications from "../components/DashboardNotifications"
import TutorialModal from "../components/TutorialModal"
import ManagerRequestsPanel from "../components/ManagerRequestsPanel"
import PayScoreGauge from "../components/PayScoreGauge"
import GlobalWalletCard from "../components/GlobalWalletCard"
import GlobalWalletSettingsModal from "../components/GlobalWalletSettingsModal"
import GlobalWalletTransactionsModal from "../components/GlobalWalletTransactionsModal"
import RegisterUtBillsCompanyModal from "./utbills/RegisterUtBillsCompanyModal"
import { getUtilityDebtSummary, getUtilityWalletBalance, getVisibleUtilitySettlementRows } from "./utbills/utilitySettlement"
import { useNavigate } from "react-router-dom"
import { getApiErrorMessage } from "../utils/errorMessage"
import { hasSeenSupportConversationsFeature } from "../utils/supportFeatureBadge"
// eslint-disable-next-line no-unused-vars
import { formatMoney, formatDateLong, formatDateShort, formatDateWithTime, formatDateInputValue, getNairobiMonth, getNairobiYear } from "../utils/format"

const getWalletAmountIn = (row) => {
  const amount = Number(row?.amount || 0)
  return amount > 0 ? amount : 0
}

const getWalletAmountOut = (row) => {
  const amount = Number(row?.amount || 0)
  return amount < 0 ? Math.abs(amount) : 0
}

const getWalletRunningBalance = (row) => {
  const balance = Number(row?.runningBalance ?? row?.balanceAfter)
  return Number.isFinite(balance) ? balance : null
}

function Dashboard() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [walletBalance, setWalletBalance] = useState(0)
  const [groupWalletBalance, setGroupWalletBalance] = useState(0)
  const [utBillsWalletBalance, setUtBillsWalletBalance] = useState(0)
  // Amount owed per module (invoiced - paid), shown on the collapsed module rows.
  const [groupOutstanding, setGroupOutstanding] = useState(0)
  const [utBillsOutstanding, setUtBillsOutstanding] = useState(0)
  const [payScoreSummary, setPayScoreSummary] = useState(null)
  const [payScoreLoading, setPayScoreLoading] = useState(false)
  // Distinct from payScoreLoading: false until the first fetch settles, so the
  // card can hold its space from the very first render instead of popping in
  // once data arrives (the card is legitimately absent for a user with no
  // score at all, but we only know that once this is true).
  const [payScoreChecked, setPayScoreChecked] = useState(false)
  const [payScoreError, setPayScoreError] = useState("")
  const [pendingGroupInvites, setPendingGroupInvites] = useState([])
  const [groupInvitesLoading, setGroupInvitesLoading] = useState(false)
  const [supportUnreadCount, setSupportUnreadCount] = useState(0)
  const [showSupportFeatureBadge, setShowSupportFeatureBadge] = useState(false)
  const [pendingWelfareApprovalGroups, setPendingWelfareApprovalGroups] = useState([])
  const [userGroupCount, setUserGroupCount] = useState(null)
  const [showJoinGroupModal, setShowJoinGroupModal] = useState(false)
  const [welfareApprovalsLoading, setWelfareApprovalsLoading] = useState(false)
  const [showPayScoreInfo, setShowPayScoreInfo] = useState(false)
  const [showAllFrpRows, setShowAllFrpRows] = useState(false)
  const [showAllGroupRows, setShowAllGroupRows] = useState(false)
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState('')
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [showTutorialModal, setShowTutorialModal] = useState(false)
  const [showManagementModal, setShowManagementModal] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [editMode, setEditMode] = useState(false)
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phone: '',
    bankName: '',
    bankAccNumber: '',
    paybill: '',
    currentPin: '',
    newPin: '',
    confirmPin: ''
  })
  const [managementForm, setManagementForm] = useState({
    companyName: '',
    telephoneNumber: '',
    headOfficeLocation: '',
    yourPositionInCompany: '',
    ownerNames: ''
  })

  // FRP Wallet transactions states
  const [showWalletTransactionsModal, setShowWalletTransactionsModal] = useState(false)
  const [walletTransactions, setWalletTransactions] = useState([])
  const [walletTransactionsLoading, setWalletTransactionsLoading] = useState(false)
  const [walletStartDate, setWalletStartDate] = useState('')
  const [walletEndDate, setWalletEndDate] = useState('')
  const [walletFilterOpen, setWalletFilterOpen] = useState(false)
  const [showAddFundsForm, setShowAddFundsForm] = useState(false)
  const [addFundsAmount, setAddFundsAmount] = useState('')
  const [showFrpTopUpButton, setShowFrpTopUpButton] = useState(false)
  const [autoDisburseSaving, setAutoDisburseSaving] = useState(false)
  // Groups Wallet transactions states
  const [showGroupWalletTransactionsModal, setShowGroupWalletTransactionsModal] = useState(false)
  const [groupWalletTransactions, setGroupWalletTransactions] = useState([])
  const [groupWalletTransactionsLoading, setGroupWalletTransactionsLoading] = useState(false)
  const [groupWalletStartDate, setGroupWalletStartDate] = useState('')
  const [groupWalletEndDate, setGroupWalletEndDate] = useState('')
  const [groupWalletFilterOpen, setGroupWalletFilterOpen] = useState(false)
  const [showAddGroupWalletFundsForm, setShowAddGroupWalletFundsForm] = useState(false)
  const [addGroupWalletFundsAmount, setAddGroupWalletFundsAmount] = useState('')
  const [showUtBillsTransactionsModal, setShowUtBillsTransactionsModal] = useState(false)
  const [utBillsStatementRows, setUtBillsStatementRows] = useState([])
  const [utBillsStatementLoading, setUtBillsStatementLoading] = useState(false)
  const [utBillsTriggerPayLoading, setUtBillsTriggerPayLoading] = useState(false)
  const [showUtPayConfirm, setShowUtPayConfirm] = useState(false)
  const [showAddUtBillsFundsForm, setShowAddUtBillsFundsForm] = useState(false)
  const [addUtBillsFundsAmount, setAddUtBillsFundsAmount] = useState('')
  const [showUtBillsTopUpButton, setShowUtBillsTopUpButton] = useState(false)
  // Only surface an "Open" entry into UT Bills when the user runs a company or
  // has bills; company registration now lives in the Register menu modal.
  const [utBillsCanOpen, setUtBillsCanOpen] = useState(false)
  const [showRegisterUtBillsModal, setShowRegisterUtBillsModal] = useState(false)
  // Global wallet (all module wallets combined)
  const [showGlobalWalletSettings, setShowGlobalWalletSettings] = useState(false)
  const [showGlobalWalletTransactions, setShowGlobalWalletTransactions] = useState(false)

  // Banks catalog for bank dropdown and paybill auto-fill
  const [banksCatalog, setBanksCatalog] = useState([])

  // Helper: previous + current month date range in YYYY-MM-DD (Africa/Nairobi time)
  const getPreviousAndCurrentMonthRange = () => {
    const y = getNairobiYear()
    const m = getNairobiMonth()
    const start = new Date(y, m - 2, 1)
    const end = new Date(y, m, 0)
    return { start: formatDateInputValue(start), end: formatDateInputValue(end) }
  }

  const navigate = useNavigate()

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token")
      if (!token) return navigate("/login")

      try {
        const res = await api.get("/auth/me")
        if (res?.data?.locked) {
          navigate('/locked')
          return
        }
        setUser(res.data)
        setShowSupportFeatureBadge(
          res.data?.role !== 'A' && !hasSeenSupportConversationsFeature(res.data?._id)
        )
        // Initialize profile form with user data
        setProfileForm({
          name: res.data.name || '',
          email: res.data.email || '',
          phone: res.data.phone || '',
          bankName: res.data.bankName || '',
          bankAccNumber: res.data.bankAccNumber || '',
          paybill: res.data.paybill || '',
          currentPin: '',
          newPin: '',
          confirmPin: ''
        })
        // Fetch wallet balance after getting user data
        fetchWalletBalance()
        fetchGroupWalletBalance()
        fetchUtBillsWalletBalance()
        fetchGroupOutstanding()
        fetchUtBillsOutstanding()
        fetchPayScoreSummary()
        fetchSupportSummary()
        fetchFrpUiFlags()
      } catch (err) {
        console.error(err)
        localStorage.removeItem("token")
        navigate("/login")
      } finally {
        setLoading(false)
      }
    }

    fetchUser()
  }, [navigate])

  // Load banks catalog (JSON preferred; fallback to xlsx if available)
  useEffect(() => {
    let mounted = true
    const loadBanks = async () => {
      try {
        let response = await fetch('/bankslist.json')
        if (response.ok) {
          const ct = (response.headers.get('content-type') || '').toLowerCase()
          if (ct.includes('application/json')) {
            const data = await response.json()
            const list = Array.isArray(data) ? data.filter(b => b && b.name) : []
            if (mounted) setBanksCatalog(list)
            return
          }
        }
        // Fallback: parse xlsx if present and xlsx lib is available
        try {
          const XLSX = await import('xlsx')
          response = await fetch('/bankslist.xlsx')
          if (response.ok) {
            const arrayBuffer = await response.arrayBuffer()
            const wb = XLSX.read(arrayBuffer, { type: 'array' })
            const ws = wb.Sheets[wb.SheetNames[0]]
            const json = XLSX.utils.sheet_to_json(ws, { header: 1 })
            const rows = Array.isArray(json) ? json : []
            const header = rows[0] || []
            const nameIdx = header.findIndex(h => String(h).toLowerCase() === 'name')
            const paybillIdx = header.findIndex(h => String(h).toLowerCase() === 'paybill')
            const list = rows
              .slice(1)
              .map(r => ({ name: r[nameIdx] || '', paybill: r[paybillIdx] || '' }))
              .filter(b => b.name)
            if (mounted) setBanksCatalog(list)
          }
        } catch (e) {
          console.warn('XLSX not available; skipping banks xlsx parsing')
        }
      } catch (e) {
        console.warn('Failed to load banks catalog', e)
      }
    }
    loadBanks()
    return () => { mounted = false }
  }, [])

  const fetchWalletBalance = async () => {
    try {
      const response = await api.get("/wallet/my-wallet")
      if (response.data.success && response.data.wallet) {
        setWalletBalance(response.data.wallet.balance)
      }
    } catch (error) {
      console.error("Error fetching wallet balance:", error)
      setWalletBalance(0)
    }
  }

  const fetchGroupWalletBalance = async () => {
    try {
      const response = await api.get("/wallet/groups/my-wallet")
      if (response.data.success && response.data.wallet) {
        setGroupWalletBalance(response.data.wallet.balance)
      }
    } catch (error) {
      console.error("Error fetching Groups wallet balance:", error)
      setGroupWalletBalance(0)
    }
  }

  const fetchUtBillsWalletBalance = async () => {
    try {
      const response = await api.get("/ut-bills/my-wallet")
      if (response.data.success && (response.data.wallet || response.data.walletBalance != null)) {
        const nextWalletBalance = getUtilityWalletBalance(response.data)
        setUtBillsWalletBalance(nextWalletBalance)
        setShowUtBillsTopUpButton(response.data.topUpEnabled === true)
        // Show "Open" when the user runs a company, has bills, or has any wallet
        // funds. The balance fallback keeps this working even before the server
        // ships the hasCompany/hasBills flags.
        let canOpen =
          Boolean(response.data.hasCompany || response.data.hasBills) ||
          nextWalletBalance > 0
        // Fallback for servers that don't yet send hasBills: a tenant with bills
        // but no wallet funds must still get an Open button. Only runs when the
        // wallet alone hasn't already granted access.
        if (!canOpen) {
          try {
            const invoiceRes = await api.get("/ut-bills/my-invoices")
            canOpen = Array.isArray(invoiceRes?.data?.invoices) && invoiceRes.data.invoices.length > 0
          } catch (invoiceError) {
            console.error("Error checking UT Bills invoices:", invoiceError)
          }
        }
        setUtBillsCanOpen(canOpen)
      }
    } catch (error) {
      console.error("Error fetching UT Bills wallet balance:", error)
      setUtBillsWalletBalance(0)
      setShowUtBillsTopUpButton(false)
      setUtBillsCanOpen(false)
    }
  }

  const fetchGroupOutstanding = async () => {
    try {
      const response = await api.get("/groups/my-outstanding")
      setGroupOutstanding(Number(response?.data?.outstanding || 0))
    } catch (error) {
      console.error("Error fetching Groups outstanding:", error)
      setGroupOutstanding(0)
    }
  }

  const fetchUtBillsOutstanding = async () => {
    try {
      const response = await api.get("/ut-bills/my-outstanding")
      setUtBillsOutstanding(getUtilityDebtSummary(response?.data || {}).amountDue)
    } catch (error) {
      console.error("Error fetching UT Bills outstanding:", error)
      setUtBillsOutstanding(0)
    }
  }

  const fetchPayScoreSummary = async () => {
    try {
      setPayScoreLoading(true)
      setPayScoreError("")
      const response = await api.get("/payscore/me")
      setPayScoreSummary(response?.data || null)
    } catch (error) {
      setPayScoreSummary(null)
      setPayScoreError(error?.response?.data?.error || error?.message || "Failed to load PaySCORE.")
    } finally {
      setPayScoreLoading(false)
      setPayScoreChecked(true)
    }
  }

  const fetchSupportSummary = async () => {
    try {
      const response = await api.get("/auth/support-tickets/summary")
      setSupportUnreadCount(Number(response?.data?.unreadCount || 0))
    } catch (error) {
      console.error("Error fetching support summary:", error)
      setSupportUnreadCount(0)
    }
  }

  const fetchFrpUiFlags = async () => {
    try {
      const response = await api.get("/frp/ui-flags")
      setShowFrpTopUpButton(response?.data?.topUpShow === true)
    } catch (error) {
      setShowFrpTopUpButton(false)
    }
  }

  const handleFrpAutoDisburseToggle = async (nextValue) => {
    if (autoDisburseSaving) return
    if (
      nextValue &&
      (
        typeof user?.bankName !== "string" ||
        user.bankName.trim().length === 0 ||
        typeof user?.bankAccNumber !== "string" ||
        user.bankAccNumber.trim().length === 0
      )
    ) {
      alert("Set bank name and bank account number in Profile before enabling auto disburse.")
      return
    }

    try {
      setAutoDisburseSaving(true)
      const response = await api.put("/auth/me/auto-disburse", { autoDisburse: nextValue })
      if (response?.data?.user) {
        setUser(response.data.user)
      }
    } catch (error) {
      console.error("Error updating auto disburse:", error)
      alert(getApiErrorMessage(error, "Failed to update auto disburse."))
    } finally {
      setAutoDisburseSaving(false)
    }
  }

  const fetchPendingGroupInvites = async () => {
    try {
      setGroupInvitesLoading(true)
      const response = await api.get("/groups/invites")
      const invites = Array.isArray(response?.data?.invites) ? response.data.invites : []
      const pending = invites.filter((inv) => String(inv?.status || "").toLowerCase() === "pending")
      setPendingGroupInvites(pending)
    } catch (error) {
      console.error("Error fetching pending group invites:", error)
      setPendingGroupInvites([])
    } finally {
      setGroupInvitesLoading(false)
    }
  }

  const fetchPendingWelfareApprovalNotifications = async () => {
    const userModules = Array.isArray(user?.assignedModules) ? user.assignedModules : []
    if (!userModules.includes("Groups")) {
      setPendingWelfareApprovalGroups([])
      setUserGroupCount(0)
      return
    }

    try {
      setWelfareApprovalsLoading(true)
      const groupsRes = await api.get("/groups?view=home")
      const groups = Array.isArray(groupsRes?.data?.groups) ? groupsRes.data.groups : []
      setUserGroupCount(groups.length)
      const adminWelfareGroups = groups.filter(
        (group) =>
          !!group?.isWelfare &&
          String(group?.myRole || "").toLowerCase() === "admin" &&
          !!group?.id
      )

      if (adminWelfareGroups.length === 0) {
        setPendingWelfareApprovalGroups([])
        return
      }

      const responses = await Promise.all(
        adminWelfareGroups.map(async (group) => {
          try {
            const res = await api.get(
              `/groups/${group.id}/welfare/loss-reports/approval-requests`,
              { params: { status: "pending" } }
            )
            const requests = Array.isArray(res?.data?.approvalRequests) ? res.data.approvalRequests : []
            if (requests.length === 0) return null
            return {
              groupId: String(group.id),
              groupName: String(group?.name || "Group"),
              pendingCount: requests.length,
              latestCreatedAt: requests[0]?.createdAt || null,
            }
          } catch (error) {
            return null
          }
        })
      )

      const notifications = responses
        .filter(Boolean)
        .sort((a, b) => Number(b?.pendingCount || 0) - Number(a?.pendingCount || 0))

      setPendingWelfareApprovalGroups(notifications)
    } catch (error) {
      console.error("Error fetching welfare approval notifications:", error)
      setPendingWelfareApprovalGroups([])
    } finally {
      setWelfareApprovalsLoading(false)
    }
  }

  const fetchWalletTransactions = async (startDate = '', endDate = '') => {
    try {
      setWalletTransactionsLoading(true)
      let url = "/wallet/transactions"
      const params = new URLSearchParams()
      const { start: defaultStart, end: defaultEnd } = getPreviousAndCurrentMonthRange()
      const effectiveStart = startDate || defaultStart
      const effectiveEnd = endDate || defaultEnd
      if (effectiveStart) params.append('startDate', effectiveStart)
      if (effectiveEnd) params.append('endDate', effectiveEnd)
      // Reflect defaults in UI filter state
      setWalletStartDate(effectiveStart)
      setWalletEndDate(effectiveEnd)
      if (params.toString()) url += `?${params.toString()}`

      const response = await api.get(url)
      setWalletTransactions(response.data.transactions)
    } catch (error) {
      console.error("Error fetching wallet transactions:", error)
      alert(
        "Error fetching wallet transactions: " +
          getApiErrorMessage(error, "Failed to fetch wallet transactions.")
      )
    } finally {
      setWalletTransactionsLoading(false)
    }
  }

  const fetchFilteredWalletTransactions = () => {
    fetchWalletTransactions(walletStartDate, walletEndDate)
  }

  const clearWalletDateFilters = () => {
    setWalletStartDate('')
    setWalletEndDate('')
    fetchWalletTransactions()
  }

  const fetchGroupWalletTransactions = async (startDate = '', endDate = '') => {
    try {
      setGroupWalletTransactionsLoading(true)
      let url = "/wallet/groups/transactions"
      const params = new URLSearchParams()
      const { start: defaultStart, end: defaultEnd } = getPreviousAndCurrentMonthRange()
      const effectiveStart = startDate || defaultStart
      const effectiveEnd = endDate || defaultEnd
      if (effectiveStart) params.append('startDate', effectiveStart)
      if (effectiveEnd) params.append('endDate', effectiveEnd)
      setGroupWalletStartDate(effectiveStart)
      setGroupWalletEndDate(effectiveEnd)
      if (params.toString()) url += `?${params.toString()}`

      const response = await api.get(url)
      setGroupWalletTransactions(response.data.transactions)
    } catch (error) {
      console.error("Error fetching Groups wallet transactions:", error)
      alert(
        "Error fetching Groups wallet transactions: " +
          getApiErrorMessage(error, "Failed to fetch Groups wallet transactions.")
      )
    } finally {
      setGroupWalletTransactionsLoading(false)
    }
  }

  const fetchFilteredGroupWalletTransactions = () => {
    fetchGroupWalletTransactions(groupWalletStartDate, groupWalletEndDate)
  }

  const clearGroupWalletDateFilters = () => {
    setGroupWalletStartDate('')
    setGroupWalletEndDate('')
    fetchGroupWalletTransactions()
  }

  const fetchUtBillsStatement = async () => {
    try {
      setUtBillsStatementLoading(true)
      const response = await api.get("/utilities/my-transactions")
      const rows = getVisibleUtilitySettlementRows(response?.data?.transactions)
      const balance = getUtilityWalletBalance(response?.data || {})
      setUtBillsStatementRows(rows)
      setUtBillsWalletBalance(balance)
      if (rows.length > 0 || balance !== 0) {
        setUtBillsCanOpen(true)
      }
    } catch (error) {
      console.error("Error fetching UT Bills statement:", error)
      alert(
        "Error fetching UT Bills statement: " +
          getApiErrorMessage(error, "Failed to fetch UT Bills statement.")
      )
    } finally {
      setUtBillsStatementLoading(false)
    }
  }

  const addFunds = async (e) => {
    e.preventDefault()
    try {
      const amount = parseFloat(addFundsAmount)
      if (isNaN(amount) || amount <= 0) {
        alert('Please enter a valid amount')
        return
      }

      await api.post('/wallet/add-funds', { amount })
      setAddFundsAmount('')
      setShowAddFundsForm(false)
      fetchWalletBalance()
      if (showWalletTransactionsModal) {
        const { start, end } = getPreviousAndCurrentMonthRange()
        fetchWalletTransactions(start, end)
      }
      alert(`${formatMoney(amount)} added to your wallet successfully!`)
    } catch (error) {
      alert("Error adding funds: " + getApiErrorMessage(error, "Failed to add funds."))
    }
  }

  const addGroupWalletFunds = async (e) => {
    e.preventDefault()
    try {
      const amount = parseFloat(addGroupWalletFundsAmount)
      if (isNaN(amount) || amount <= 0) {
        alert('Please enter a valid amount')
        return
      }

      await api.post('/wallet/groups/add-funds', { amount })
      setAddGroupWalletFundsAmount('')
      setShowAddGroupWalletFundsForm(false)
      fetchGroupWalletBalance()
      if (showGroupWalletTransactionsModal) {
        const { start, end } = getPreviousAndCurrentMonthRange()
        fetchGroupWalletTransactions(start, end)
      }
      alert(`${formatMoney(amount)} added to your Groups Wallet successfully!`)
    } catch (error) {
      alert(
        "Error adding funds to Groups Wallet: " +
          getApiErrorMessage(error, "Failed to add funds to Groups Wallet.")
      )
    }
  }

  const addUtBillsFunds = async (e) => {
    e.preventDefault()
    try {
      const amount = parseFloat(addUtBillsFundsAmount)
      if (isNaN(amount) || amount <= 0) {
        alert('Please enter a valid amount')
        return
      }

      await api.post('/ut-bills/add-funds', { amount })
      setAddUtBillsFundsAmount('')
      setShowAddUtBillsFundsForm(false)
      fetchUtBillsWalletBalance()
      if (showUtBillsTransactionsModal) {
        fetchUtBillsStatement()
      }
      alert(`${formatMoney(amount)} added to your UT Bills Wallet successfully!`)
    } catch (error) {
      alert(
        "Error adding funds to UT Bills Wallet: " +
          getApiErrorMessage(error, "Failed to add funds to UT Bills Wallet.")
      )
    }
  }

  // Moves money out of many tenant wallets at once, so it always asks first.
  const triggerManagerUtPay = async () => {
    try {
      setShowUtPayConfirm(false)
      setUtBillsTriggerPayLoading(true)
      const response = await api.post('/utilities/manager-settle')
      const summary = response?.data?.summary || {}
      const tenantsReviewed = Number(summary?.tenantsReviewed || 0)
      const tenantsTriggered = Number(summary?.tenantsTriggered || 0)
      const paymentsProcessed = Number(summary?.paymentsProcessed || 0)
      const totalPaid = Number(summary?.totalPaid || 0)

      alert(
        response?.data?.message ||
        `UT pay trigger completed. Reviewed ${tenantsReviewed} tenant wallet${tenantsReviewed === 1 ? '' : 's'}, processed ${paymentsProcessed} payment${paymentsProcessed === 1 ? '' : 's'} across ${tenantsTriggered} tenant${tenantsTriggered === 1 ? '' : 's'}, total applied ${formatMoney(totalPaid)}.`
      )

      fetchUtBillsWalletBalance()
      if (showUtBillsTransactionsModal) {
        fetchUtBillsStatement()
      }
    } catch (error) {
      alert(
        "Error triggering UT pay: " +
        getApiErrorMessage(error, "Failed to trigger utility payments.")
      )
    } finally {
      setUtBillsTriggerPayLoading(false)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('.dropdown-container')) {
        setIsMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  const handleLogout = () => {
    localStorage.removeItem("token")
    navigate("/login")
  }

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const handleManagementClick = () => {
    setIsMenuOpen(false)
    setShowManagementModal(true)
    setFormError('')
    // Reset management form
    setManagementForm({
      companyName: '',
      telephoneNumber: '',
      headOfficeLocation: '',
      yourPositionInCompany: '',
      ownerNames: ''
    })
  }

  const handleGroupClick = () => {
    setIsMenuOpen(false)
    navigate("/app/groups", { state: { openCreate: true } })
  }

  const handleProfileClick = () => {
    setShowProfileModal(true)
    setEditMode(false)
    setFormError('')
    // Reset form with current user data
    setProfileForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      bankName: user.bankName || '',
      bankAccNumber: user.bankAccNumber || '',
      paybill: user.paybill || '',
      currentPin: '',
      newPin: '',
      confirmPin: ''
    })
  }

  // eslint-disable-next-line no-unused-vars
  const handleProfileSave = async (e) => {
    e.preventDefault()
    setFormLoading(true)
    setFormError('')

    // Validate PIN change if provided
    if (profileForm.newPin) {
      if (!profileForm.currentPin) {
        setFormError('Current PIN is required to change PIN')
        setFormLoading(false)
        return
      }
      if (profileForm.newPin !== profileForm.confirmPin) {
        setFormError('New PIN and confirmation do not match')
        setFormLoading(false)
        return
      }
      if (!/^\d{4}$/.test(profileForm.newPin)) {
        setFormError('New PIN must be exactly 4 digits')
        setFormLoading(false)
        return
      }
    }

    try {
      const updateData = {
        name: profileForm.name,
        email: profileForm.email,
        phone: profileForm.phone,
        bankName: profileForm.bankName,
        bankAccNumber: profileForm.bankAccNumber,
        paybill: profileForm.paybill
      }

      // Only include PIN data if changing PIN
      if (profileForm.newPin) {
        updateData.currentPin = profileForm.currentPin
        updateData.newPin = profileForm.newPin
      }

      const response = await api.put('/auth/profile', updateData)
      setUser(response.data.user)
      setEditMode(false)
      setFormError('')
      // Reset PIN fields
      setProfileForm({
        name: response.data.user.name || '',
        email: response.data.user.email || '',
        phone: response.data.user.phone || '',
        bankName: response.data.user.bankName || '',
        bankAccNumber: response.data.user.bankAccNumber || '',
        paybill: response.data.user.paybill || '',
        currentPin: '',
        newPin: '',
        confirmPin: ''
      })
      alert('Profile updated successfully!')
    } catch (error) {
      setFormError('Error updating profile: ' + (error.response?.data?.error || error.message))
    } finally {
      setFormLoading(false)
    }
  }

  const handleManagementSave = async (e) => {
    e.preventDefault()
    setFormLoading(true)
    setFormError('')

    try {
      const response = await api.post('/management-company/create', managementForm)

      // Update token if provided in response
      if (response.data.newToken) {
        localStorage.setItem('token', response.data.newToken)
      }

      // Refresh user data to get updated role and modules
      const userRes = await api.get("/auth/me")
      setUser(userRes.data)

      setShowManagementModal(false)
      setManagementForm({
        companyName: '',
        telephoneNumber: '',
        headOfficeLocation: '',
        yourPositionInCompany: '',
        ownerNames: ''
      })
      alert("Management company created successfully!")
    } catch (err) {
      setFormError(err.response?.data?.error || 'Network error. Please try again.')
    } finally {
      setFormLoading(false)
    }
  }

  const getInitials = (name) => {
    if (!name) return 'U'
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase()
  }

  const getRoleInfo = (role) => {
    const roles = {
      M: { name: "Management", color: "#8b5cf6", bgColor: "#f3f4f6" },
      L: { name: "Landlord", color: "#3b82f6", bgColor: "#eff6ff" },
      T: { name: "Tenant", color: "#10b981", bgColor: "#ecfdf5" },
      A: { name: "Admin", color: "#ef4444", bgColor: "#fef2f2" },
      Mbr: { name: "Member", color: "#f59e0b", bgColor: "#fffbeb" },
    }
    return roles[role] || { name: "Unknown", color: "#6b7280", bgColor: "#f9fafb" }
  }

  const resolvePayScoreStatusClasses = (status) => {
    const normalized = String(status || "").toLowerCase()
    if (normalized === "excellent") return "bg-emerald-50 text-emerald-700 border-emerald-200"
    if (normalized === "good") return "bg-green-50 text-green-700 border-green-200"
    if (normalized === "fair") return "bg-amber-50 text-amber-700 border-amber-200"
    if (normalized === "risk") return "bg-orange-50 text-orange-700 border-orange-200"
    if (normalized === "default" || normalized === "restricted") return "bg-red-50 text-red-700 border-red-200"
    return "bg-gray-50 text-gray-700 border-gray-200"
  }

  const rlPayScoreValue = Number(payScoreSummary?.final?.score)
  const rlPayScoreText = Number.isFinite(rlPayScoreValue)
    ? `${rlPayScoreValue.toFixed(1)}%`
    : (payScoreLoading ? "..." : "--")
  const rlPayScoreStatus = payScoreSummary?.final?.status || "N/A"
  const frpPayScoreValue = Number(payScoreSummary?.modules?.frp?.score)
  const groupPayScoreValue = Number(payScoreSummary?.modules?.group?.score)
  const hasFrpPayScore = Number.isFinite(frpPayScoreValue) && frpPayScoreValue > 0
  const hasGroupPayScore = Number.isFinite(groupPayScoreValue) && groupPayScoreValue > 0
  const frpPayScoreText = hasFrpPayScore ? `${frpPayScoreValue.toFixed(1)}%` : "--"
  const groupPayScoreText = hasGroupPayScore ? `${groupPayScoreValue.toFixed(1)}%` : "--"
  const userModules = Array.isArray(user?.assignedModules) ? user.assignedModules : []
  const hasFrpModule = userModules.includes("FRP")
  const hasGroupsModule = userModules.includes("Groups")
  const walletCardModules = Array.from(new Set(["FRP", "Groups", "UTBills", ...userModules]))
  const finalFormulaText = hasFrpPayScore && hasGroupPayScore
    ? `Final RL = (${frpPayScoreText} + ${groupPayScoreText}) / 2 = ${rlPayScoreText}`
    : hasFrpPayScore
      ? `You currently use FRP scoring only, so RL matches FRP: ${frpPayScoreText}.`
      : hasGroupPayScore
        ? `You currently use Groups scoring only, so RL matches Groups: ${groupPayScoreText}.`
        : "No module score is available yet."
  const defaultPenalty = Number(payScoreSummary?.rules?.defaultPenalty ?? 25)
  const frpLateWeight = Number(payScoreSummary?.rules?.frpBaseScore ?? 30)
  const formatScoreValue = (value) => {
    const num = Number(value)
    return Number.isFinite(num) ? `${num.toFixed(0)}%` : "--"
  }
  const frpBreakdownRows = Array.isArray(payScoreSummary?.modules?.frp?.units)
    ? payScoreSummary.modules.frp.units
    : []
  const groupsBreakdownRows = Array.isArray(payScoreSummary?.modules?.group?.groups)
    ? payScoreSummary.modules.group.groups
    : []
  const visibleFrpBreakdownRows = showAllFrpRows ? frpBreakdownRows : frpBreakdownRows.slice(0, 4)
  const visibleGroupsBreakdownRows = showAllGroupRows ? groupsBreakdownRows : groupsBreakdownRows.slice(0, 4)
  const frpTotalOutstanding = frpBreakdownRows.reduce((sum, unit) => sum + (unit.currentOutstandingBalance || 0), 0)

  useEffect(() => {
    if (!user) return undefined

    fetchPendingGroupInvites()
    fetchPendingWelfareApprovalNotifications()

    const timer = setInterval(() => {
      fetchPendingGroupInvites()
      fetchPendingWelfareApprovalNotifications()
    }, 15000)

    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id])

  if (loading) {
    // Mirrors the real dashboard block for block: sticky header, welcome card,
    // PaySCORE card, wallet card, then the modules panel.
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 font-sans">
        <header className="sticky top-0 z-10 border-b border-gray-200 bg-white/80 backdrop-blur-sm">
          <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-2 px-3 sm:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <Skeleton className="h-10 w-10 shrink-0" rounded="rounded-xl" />
              <div className="min-w-0 space-y-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="hidden h-2.5 w-24 sm:block" />
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Skeleton className="h-8 w-8" rounded="rounded-lg" />
              <Skeleton className="h-8 w-8" rounded="rounded-full" />
              <Skeleton className="h-8 w-8" rounded="rounded-lg" />
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-6xl px-6" aria-busy="true" aria-label="Loading your dashboard">
          {/* Welcome card */}
          <div className="mb-4 mt-3 flex items-center gap-3 rounded-2xl border bg-white px-4 py-3 shadow-sm">
            <Skeleton className="h-12 w-12 shrink-0" rounded="rounded-xl" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-2.5 w-24" />
              <Skeleton className="h-4 w-40" />
            </div>
            <Skeleton className="h-6 w-16 shrink-0" rounded="rounded-lg" />
          </div>

          {/* PaySCORE card: header strip plus the gauge */}
          <div className="mb-6 overflow-hidden rounded-2xl border border-green-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-green-100 bg-green-50/60 px-4 py-3">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-8 w-20" rounded="rounded-lg" />
            </div>
            <div className="flex justify-center px-4 py-6">
              <Skeleton className="h-28 w-56" rounded="rounded-t-full" />
            </div>
          </div>

          {/* Global wallet card */}
          <div className="mb-6 rounded-2xl border border-green-200 bg-white px-4 py-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1 space-y-2.5">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-8 w-44" />
              </div>
              <div className="flex shrink-0 gap-2">
                <Skeleton className="h-9 w-9" rounded="rounded-lg" />
                <Skeleton className="h-9 w-9" rounded="rounded-lg" />
              </div>
            </div>
          </div>

          {/* Modules panel */}
          <div className="overflow-hidden rounded-2xl border border-green-200/70 bg-white/80 shadow-lg backdrop-blur-sm">
            <div className="flex items-center justify-between border-b border-green-100 bg-green-50/60 px-4 py-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-8 w-20" rounded="rounded-lg" />
            </div>
            <div className="p-4">
              <SkeletonList rows={4} />
            </div>
          </div>
        </main>
      </div>
    )
  }

  if (!user) return null

  const roleInfo = getRoleInfo(user.role)

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 font-sans">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 flex justify-between items-center h-16 gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/logofinalrounded.png"
              alt="RecordLOGS logo"
              className="w-10 h-10 rounded-xl object-cover shadow-sm"
            />
            <div className="min-w-0">
              <h1 className="truncate text-lg sm:text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                RecordLOGS
              </h1>
              <p className="hidden sm:block text-xs text-gray-500">Management System</p>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-3 shrink-0">
            <div className="hidden md:flex items-center gap-2">
              <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-green-100 text-green-800 border border-green-200 text-xs font-semibold whitespace-nowrap">
                FRP Wallet: {formatMoney(walletBalance)}
              </span>
              <span
                className={`inline-flex items-center px-3 py-1.5 rounded-lg border text-xs font-semibold whitespace-nowrap ${resolvePayScoreStatusClasses(rlPayScoreStatus)}`}
                title={payScoreError || `RL PaySCORE status: ${rlPayScoreStatus}`}
              >
                RL PaySCORE: {rlPayScoreText}
              </span>
            </div>

            <DashboardNotifications
              inviteCount={pendingGroupInvites.length}
              welfareGroups={pendingWelfareApprovalGroups}
              loading={groupInvitesLoading || welfareApprovalsLoading}
              onOpenInvites={() => navigate("/app/groups", { state: { openInvites: true } })}
              onOpenGroup={(groupId) =>
                navigate(`/app/groups?groupId=${encodeURIComponent(String(groupId || ""))}`)
              }
            />

            <button
              onClick={() => navigate("/support-tickets")}
              className="relative hidden sm:flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg text-sm font-medium transition-all"
              aria-label="Open support tickets"
            >
              <Icon name="bell" size="1.1rem" />
              <span>Support</span>
              {supportUnreadCount > 0 ? (
                <span className="absolute -right-1 -top-1 min-w-[1.25rem] rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                  {supportUnreadCount > 99 ? '99+' : supportUnreadCount}
                </span>
              ) : null}
            </button>

            {/* Profile Button */}
            <button
              onClick={handleProfileClick}
              className="flex items-center gap-2 px-2 sm:px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg text-sm font-medium transition-all min-w-0"
            >
              {user?.profilePhoto?.url ? (
                <img
                  src={user.profilePhoto.url}
                  alt="Profile"
                  className="w-8 h-8 rounded-full object-cover border"
                />
              ) : (
                <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                  {getInitials(user.name)}
                </div>
              )}
              <span className="hidden md:inline-block max-w-[140px] truncate">
                {user?.name || "User"}
              </span>
              <Icon name="person" size="1.1rem" className="hidden sm:inline-block" />
            </button>

            {/* User Profile Dropdown */}
            <div className="relative dropdown-container">
              <button
                onClick={toggleMenu}
                data-tutorial="nav-menu-btn"
                className="relative flex items-center gap-2 px-2 sm:px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg text-sm font-medium transition-all"
              >
                <Icon name="list" size="1.5rem" />
                {showSupportFeatureBadge ? (
                  <span className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-white" />
                ) : null}
              </button>

              {/* Quick Add Funds for Members (unassigned tenants) */}
              {/* {user.role === 'Mbr' && (
                <button
                  onClick={() => setShowAddFundsForm(true)}
                  className="hidden md:inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  <Icon name="plus" size="1rem" />
                  Add Funds
                </button>
              )} */}

              {/* Dropdown Menu */}
              <div className={`absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 z-20 ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
                }`}>
                <div className="p-2">
                  <h2 className="p-2">Register</h2>

                  {/* Property Management button - for creating ManagementCompany - accessible to all users */}
                  <button
                    onClick={handleManagementClick}
                    data-tutorial="nav-pm-item"
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                  >
                    <Icon name="clipboard" size="1rem" />
                    Property Management
                  </button>

                  {/* UT-Bills Company - create a utility billing company or use an existing FRP company */}
                  <button
                    onClick={() => {
                      setIsMenuOpen(false)
                      setShowRegisterUtBillsModal(true)
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                  >
                    <Icon name="shop" size="1rem" />
                    UT-Bills Company
                  </button>

                  {/* Admin Panel button - only show for admin users */}
                  {user.role === 'A' && (
                    <button
                      onClick={() => navigate("/admin")}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                    >
                      <Icon name="shield-check" size="1rem" />
                      Admin Panel
                    </button>
                  )}

                  {(user.role === 'A' || user.canUseMpesaReconciliation) && (
                    <button
                      onClick={() => navigate(user.role === 'A' ? "/admin/mpesa-reconciliation" : "/mpesa-reconciliation")}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                    >
                      <Icon name="search" size="1rem" />
                      M-Pesa Reconciliation
                    </button>
                  )}

                  <button
                    onClick={handleGroupClick}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                  >
                    <Icon name="people" size="1rem" />
                    Group
                  </button>

                  <button
                    onClick={() => navigate("/support-tickets")}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center justify-between gap-2"
                  >
                    <span className="flex items-center gap-2">
                      <Icon name="life-preserver" size="1rem" />
                      Support Tickets
                    </span>
                    <span className="flex items-center gap-2">
                      {showSupportFeatureBadge ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                          New
                        </span>
                      ) : null}
                      {supportUnreadCount > 0 ? (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                          {supportUnreadCount}
                        </span>
                      ) : null}
                    </span>
                  </button>
                  <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2">
                    <Icon name="car-front" size="1rem" />
                    Vehicle
                  </button>
                  <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2">
                    <Icon name="mortarboard" size="1rem" />
                    School
                  </button>
                  <div className="h-px bg-gray-100 my-1"></div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md flex items-center gap-2"
                  >
                    <Icon name="box-arrow-right" size="1rem" />
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6">
        {/* User Section */}
        <div className="mb-4 mt-3">
          <div
            className="flex items-center gap-3 rounded-2xl border bg-white px-4 py-3 shadow-sm"
            style={{ borderColor: roleInfo.color + '30' }}
          >
            {user?.profilePhoto?.url ? (
              <img
                src={user.profilePhoto.url}
                alt="Profile"
                className="h-12 w-12 shrink-0 rounded-xl object-cover"
              />
            ) : (
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-sm font-black"
                style={{ backgroundColor: roleInfo.bgColor, color: roleInfo.color }}
              >
                {getInitials(user.name)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: roleInfo.color }}>Welcome back</p>
              <h2 className="truncate text-[17px] font-bold leading-tight text-slate-900">
                {user.name || 'User'}
              </h2>
            </div>
            <div
              className="shrink-0 rounded-lg border px-2.5 py-1 text-[11px] font-bold"
              style={{ backgroundColor: roleInfo.bgColor, color: roleInfo.color, borderColor: roleInfo.color + '40' }}
            >
              {roleInfo.name}
            </div>
          </div>
        </div>

        {/* Manager Requests Panel - Only shows for managers with pending requests */}
        <ManagerRequestsPanel user={user} />

        {/* Group invitations and welfare loss-report approvals are reachable
            from the bell in the header, so they no longer take a full-width
            banner and push the wallet and modules down the page. */}

        {/* PayScore Section */}
        {!payScoreChecked ? (
          // Same header + gauge footprint as the real card below, sized to
          // PayScoreGauge's own dimensions, so nothing shifts once the score
          // arrives (or the card quietly resolves to nothing, for a user who
          // has never had a score).
          <div className="mb-6 rounded-2xl border border-green-200 bg-white shadow-sm">
            <div className="flex items-center justify-between rounded-t-2xl border-b border-green-100 bg-green-50/60 px-4 py-3">
              <Skeleton className="h-2.5 w-24" />
              <Skeleton className="h-7 w-20" rounded="rounded-lg" />
            </div>
            <div className="flex justify-center py-4">
              <Skeleton
                className="h-[92px] w-full max-w-[150px] sm:h-[120px] sm:max-w-[200px]"
                rounded="rounded-t-full"
              />
            </div>
          </div>
        ) : (hasFrpPayScore || hasGroupPayScore) && (
          <div className="mb-6">
            <div className="rounded-2xl border border-green-200 bg-white shadow-sm">
              <div className="flex items-center justify-between rounded-t-2xl border-b border-green-100 bg-green-50/60 px-4 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-green-600">RL PaySCORE</p>
                <button
                  type="button"
                  onClick={() => setShowPayScoreInfo(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-green-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-green-700 shadow-sm transition-colors hover:bg-green-50"
                >
                  <Icon name="info-circle" size="0.8rem" />
                  <span>Details</span>
                </button>
              </div>
              <div className="flex justify-center py-4">
                <PayScoreGauge score={rlPayScoreValue} label="RL PaySCORE" />
              </div>
            </div>

            {/* Backdrop for mobile */}
            {showPayScoreInfo && (
              <div
                className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
                onClick={() => setShowPayScoreInfo(false)}
              />
            )}

            <div
              className={`fixed z-50 top-1/2 left-1/2 w-[min(92vw,28rem)] max-h-[85vh] overflow-y-auto rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-2xl transition-all duration-200 ease-out ${showPayScoreInfo
                ? "opacity-100 -translate-x-1/2 -translate-y-1/2 pointer-events-auto scale-100"
                : "opacity-0 -translate-x-1/2 -translate-y-[45%] pointer-events-none scale-95"
                }`}
            >
              <div className="sticky top-0 bg-white/95 backdrop-blur-sm px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-[#e38545] rounded-lg text-white shadow-sm flex items-center justify-center">
                    <Icon name="star-fill" size="0.9rem" />
                  </div>
                  <p className="font-bold text-slate-900">How RL PaySCORE works</p>
                </div>
                <button
                  onClick={() => setShowPayScoreInfo(false)}
                  className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 transition-colors"
                >
                  <Icon name="x" size="1.1rem" />
                </button>
              </div>

              <div className="p-3 bg-[#f8f6fb] space-y-2">
                <p className="text-[9.5px] text-slate-500 text-center leading-snug">
                  PaySCORE reflects the <span className="font-bold text-slate-700">average</span> payment performance across all RL modules.
                </p>

                {/* Top Section: General PaySCORE info & Gauge */}
                <div className="flex gap-2 items-stretch mt-3">
                  {/* Left: General PaySCORE explanation */}
                  <div className="flex-1 rounded-xl border border-orange-200 bg-orange-50/70 p-2.5 relative overflow-hidden flex flex-col justify-between">
                    {/* Header */}
                    <div className="flex items-center gap-1.5 mb-2">
                      <div className="w-5 h-5 rounded-lg bg-[#e38545] text-white flex items-center justify-center shadow-sm flex-shrink-0">
                        <Icon name="currency-dollar" size="0.7rem" />
                      </div>
                      <p className="text-[10.5px] font-extrabold text-slate-800 leading-tight">RL PaySCORE</p>
                    </div>

                    {/* Description */}
                    <p className="text-[8.5px] text-slate-600 mb-2 leading-snug">
                      Measures payment reliability by scoring each rental unit and each group over its{' '}
                      <span className="font-bold text-slate-800">last 36 checkpoints</span>{' '}
                      <span className="text-slate-500">and then averaging those scores inside each module.</span>
                    </p>

                    {/* Rules */}
                    <div className="space-y-1 text-[8.5px] font-medium">
                      <div className="flex items-center gap-1.5">
                        <Icon name="check2" size="0.7rem" className="text-emerald-600 flex-shrink-0" />
                        <span className="text-slate-700">
                          <span className="font-bold text-slate-800">On-time</span> &rarr; 100%
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Icon name="check2" size="0.7rem" className="text-emerald-600 flex-shrink-0" />
                        <span className="text-slate-700">
                          <span className="font-bold text-slate-800">Late</span> &rarr; {frpLateWeight}%
                        </span>
                      </div>
                      <div className="flex items-start gap-1.5">
                        <Icon name="exclamation-circle-fill" size="0.65rem" className="text-[#e38545] flex-shrink-0 mt-[1px]" />
                        <span className="text-slate-700 leading-snug">
                          <span className="font-bold text-slate-800">Default</span>{' '}
                          <span className="text-slate-500">(&gt;2 unpaid payment cycles)</span>{' '}
                          &rarr; <span className="font-bold text-red-600">-{defaultPenalty}%</span>
                          <br />
                          <span className="text-slate-500 text-[7.5px]">(Negative penalty)</span>
                        </span>
                      </div>
                    </div>

                    {/* Footer note */}
                    <div className="mt-2.5 pt-2 border-t border-orange-200/60">

                    </div>
                  </div>

                  {/* Right: Gauge + Score Levels + Button */}
                  <div className="w-[48%] rounded-xl flex flex-col items-center gap-1 p-2">
                    <PayScoreGauge score={rlPayScoreValue} />

                    {/* Score Levels — stacked vertically */}
                    <div className="w-full flex flex-col gap-0.5 mt-0.5">
                      {[
                        { range: '90–100', label: 'Excellent', dot: '#10b981', text: '#047857' },
                        { range: '80–89', label: 'Good', dot: '#22c55e', text: '#15803d' },
                        { range: '70–79', label: 'Fair', dot: '#f59e0b', text: '#92400e' },
                        { range: '50–69', label: 'Risk', dot: '#f97316', text: '#9a3412' },
                        { range: '0–49', label: 'Default', dot: '#ef4444', text: '#991b1b' },
                      ].map(({ range, label, dot, text }) => (
                        <span
                          key={label}
                          className="inline-flex items-center gap-1 text-[7.5px] font-semibold px-1.5 py-[2px] rounded-full bg-white border border-gray-200 w-full"
                          style={{ color: text }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: dot }} />
                          {range}% · {label}
                        </span>
                      ))}
                    </div>

                    <button type="button" className="mt-1 w-full rounded-md bg-[#5b7a77] hover:bg-[#4a6361] text-white text-[9px] py-1.5 text-center font-bold shadow transition-colors">
                      Score Details
                    </button>
                  </div>
                </div>

                {/* Lists */}
                <div className="flex flex-col gap-2">
                  {/* Rental Units */}
                  <div className="flex-1 rounded-xl border border-[#e6e3f2] bg-[#f1eff7]/70 overflow-hidden flex flex-col">
                    <div className="px-2 py-1.5 border-b border-[#e6e3f2] flex items-center gap-1">
                      <div className="w-3.5 h-3.5 rounded-[3px] bg-blue-500 text-white flex items-center justify-center">
                        <Icon name="building-fill" size="0.45rem" />
                      </div>
                      <p className="font-bold text-slate-700 text-[10px]">Rental Units Payments</p>
                    </div>
                    <div className="p-1.5 space-y-1.5 overflow-y-auto max-h-[160px] custom-scrollbar">
                      {frpBreakdownRows.length === 0 ? (
                        <p className="text-[9px] text-slate-500 text-center py-2">No units</p>
                      ) : visibleFrpBreakdownRows.map((row, index) => (
                        <div key={`u-${index}`} className="group bg-white/50 hover:bg-white p-2 rounded-lg border border-transparent hover:border-slate-200 transition-all flex flex-col gap-1.5 shadow-sm">
                          <div className="flex justify-between items-center">
                            <div className="flex-1 truncate pr-2">
                              <p className="font-bold text-slate-800 text-[10px] leading-tight truncate">{row?.name || "Unit"}</p>
                              {row?.propertyName && (
                                <p className="text-[8px] text-slate-500 truncate mt-0.5">{row.propertyName}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              {row?.hasDefault && (
                                <span className="text-[8px] font-black bg-red-50 text-red-600 px-1.5 py-0.5 rounded-md border border-red-100 uppercase tracking-tighter">
                                  Default -{defaultPenalty}%
                                </span>
                              )}
                              <span className="text-[10px] font-black text-emerald-700 tabular-nums bg-emerald-50 px-1.5 py-0.5 rounded-md border border-emerald-100">
                                {formatScoreValue(row?.score)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-[8px] font-bold text-slate-500">
                            <div className="flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              <span>On-time: <span className="text-slate-700">{Number(row?.onTime || 0)}</span></span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                              <span>Late: <span className="text-slate-700">{Number(row?.late || 0)}</span></span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                              <span>Total: <span className="text-slate-700">{Number(row?.total || (row?.onTime || 0) + (row?.late || 0))}</span></span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {frpBreakdownRows.length > 4 && (
                      <button
                        type="button"
                        onClick={() => setShowAllFrpRows((prev) => !prev)}
                        className="mx-1 mb-1 mt-0.5 text-[9px] font-semibold text-emerald-700 hover:text-emerald-800"
                      >
                        {showAllFrpRows ? "Show less" : `Show ${frpBreakdownRows.length - 4} more`}
                      </button>
                    )}
                  </div>

                  {/* Groups */}
                  <div className="flex-1 rounded-xl border border-[#e6e3f2] bg-[#f1eff7]/70 overflow-hidden flex flex-col">
                    <div className="px-2 py-1.5 border-b border-[#e6e3f2] flex items-center gap-1">
                      <div className="w-3.5 h-3.5 rounded-[3px] bg-[#6ca590] text-white flex items-center justify-center">
                        <Icon name="people-fill" size="0.45rem" />
                      </div>
                      <p className="font-bold text-slate-700 text-[10px]">Groups Payments</p>
                    </div>
                    <div className="p-1.5 space-y-1.5 overflow-y-auto max-h-[160px] custom-scrollbar">
                      {groupsBreakdownRows.length === 0 ? (
                        <p className="text-[9px] text-slate-500 text-center py-2">No groups</p>
                      ) : visibleGroupsBreakdownRows.map((row, index) => (
                        <div key={`g-${index}`} className="group bg-white/50 hover:bg-white p-2 rounded-lg border border-transparent hover:border-slate-200 transition-all flex flex-col gap-1.5 shadow-sm">
                          <div className="flex justify-between items-center">
                            <p className="font-bold text-slate-800 text-[10px] leading-tight truncate pr-2 flex-1">{row?.name || "Group"}</p>
                            <div className="flex items-center gap-1.5">
                              {row?.hasDefault && (
                                <span className="text-[8px] font-black bg-red-50 text-red-600 px-1.5 py-0.5 rounded-md border border-red-100 uppercase tracking-tighter">
                                  Default -{defaultPenalty}%
                                </span>
                              )}
                              <span className="text-[10px] font-black text-emerald-700 tabular-nums bg-emerald-50 px-1.5 py-0.5 rounded-md border border-emerald-100">
                                {formatScoreValue(row?.score)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-[8px] font-bold text-slate-500">
                            <div className="flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              <span>On-time: <span className="text-slate-700">{Number(row?.onTime || 0)}</span></span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                              <span>Late: <span className="text-slate-700">{Number(row?.late || 0)}</span></span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                              <span>Total: <span className="text-slate-700">{Number(row?.total || (row?.onTime || 0) + (row?.late || 0))}</span></span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {groupsBreakdownRows.length > 4 && (
                      <button
                        type="button"
                        onClick={() => setShowAllGroupRows((prev) => !prev)}
                        className="mx-1 mb-1 mt-0.5 text-[9px] font-semibold text-emerald-700 hover:text-emerald-800"
                      >
                        {showAllGroupRows ? "Show less" : `Show ${groupsBreakdownRows.length - 4} more`}
                      </button>
                    )}
                  </div>
                </div>

                {!!payScoreSummary?.final?.capApplied && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-2 text-[9px] font-semibold text-amber-800 flex items-center gap-1.5 mt-2">
                    <Icon name="exclamation-triangle-fill" size="0.65rem" />
                    <span>Active default detected: capped at 79%</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Global Wallet: combined balance across all module wallets */}
        <GlobalWalletCard
          balances={{
            frp: walletBalance,
            groups: groupWalletBalance,
            utbills: utBillsWalletBalance,
          }}
          onOpenSettings={() => setShowGlobalWalletSettings(true)}
          onOpenTransactions={() => setShowGlobalWalletTransactions(true)}
        />

        {/* Modules */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-green-200/70 overflow-hidden">
          <h3 className="flex items-center justify-between bg-green-50/60 border-b border-green-100 px-4 py-3 text-gray-800 text-base font-semibold">
            <span className="flex items-center gap-2">
              <Icon name="grid" size="1.1rem" className="text-green-600" />
              Your Modules
            </span>
            <button
              onClick={() => setShowTutorialModal(true)}
              className="flex items-center gap-1.5 text-[11px] font-semibold text-green-700 bg-white hover:bg-green-100 border border-green-200 hover:border-green-300 rounded-lg px-2.5 py-1.5 transition-colors"
            >
              <Icon name="mortarboard-fill" size="0.75rem" />
              Tutorial
            </button>
          </h3>
          <div className="p-4">
          <RoleMenu
            modules={walletCardModules}
            assignedModules={userModules}
            user={user}
            payScoreSummary={payScoreSummary}
            payScoreLoading={payScoreLoading}
            onTransactionsClick={(module) => {
              if (module === 'FRP') {
                setShowWalletTransactionsModal(true)
                fetchWalletTransactions()
              } else if (module === 'Groups') {
                setShowGroupWalletTransactionsModal(true)
                fetchGroupWalletBalance()
                fetchGroupWalletTransactions()
              } else if (module === 'UTBills') {
                setShowUtBillsTransactionsModal(true)
                fetchUtBillsWalletBalance()
                fetchUtBillsStatement()
              } else {
                alert(`${module} Wallet coming soon`)
              }
            }}
            onTopUpClick={(module) => {
              if (module === 'FRP') {
                setShowAddFundsForm(true)
              } else if (module === 'Groups') {
                setShowAddGroupWalletFundsForm(true)
              } else if (module === 'UTBills') {
                setShowAddUtBillsFundsForm(true)
              } else {
                alert(`${module} Top Up coming soon`)
              }
            }}
            onUtBillsTriggerPayClick={() => setShowUtPayConfirm(true)}
            utBillsTriggerPayLoading={utBillsTriggerPayLoading}
            onFrpAutoDisburseToggle={handleFrpAutoDisburseToggle}
            frpAutoDisburseSaving={autoDisburseSaving}
            tenantBalanceDue={walletBalance}
            groupBalanceDue={groupWalletBalance}
            utBillsBalanceDue={utBillsWalletBalance}
            groupOutstanding={groupOutstanding}
            utBillsOutstanding={utBillsOutstanding}
            frpBalanceDue={frpTotalOutstanding}
            showFrpTopUpButton={showFrpTopUpButton}
            showUtBillsTopUpButton={showUtBillsTopUpButton}
            showUtBillsOpen={utBillsCanOpen}
            userGroupCount={userGroupCount}
            onJoinGroupClick={() => setShowJoinGroupModal(true)}
          />
          </div>
        </div>
      </main >

      {/* Register a UT-Bills Company Modal */}
      {showRegisterUtBillsModal && (
        <RegisterUtBillsCompanyModal onClose={() => setShowRegisterUtBillsModal(false)} />
      )}

      {/* Global Wallet Settings Modal (transfers + disbursement sources) */}
      {showGlobalWalletSettings && (
        <GlobalWalletSettingsModal
          onClose={() => setShowGlobalWalletSettings(false)}
          onBalancesChanged={() => {
            fetchWalletBalance()
            fetchGroupWalletBalance()
            fetchUtBillsWalletBalance()
          }}
        />
      )}

      {/* Global Wallet Statement Modal (all wallets combined) */}
      {showGlobalWalletTransactions && (
        <GlobalWalletTransactionsModal onClose={() => setShowGlobalWalletTransactions(false)} />
      )}

      {/* Join a Group Modal */}
      {showJoinGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 flex-shrink-0">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Join a Group</h3>
                <p className="mt-0.5 text-xs text-gray-500">Search by full Group ID, then request to join.</p>
              </div>
              <button
                onClick={() => setShowJoinGroupModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <Icon name="x" size="1.2rem" />
              </button>
            </div>
            <div className="overflow-y-auto p-5">
              <GroupSearchPanel registeredName={user?.name || ""} embedded />
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {
        showProfileModal && (
          <ProfileModal
            user={user}
            banksCatalog={banksCatalog}
            onClose={() => setShowProfileModal(false)}
            onSaved={(u) => { setUser(u); setShowProfileModal(false) }}
          />
        )
      }

      {/* Tutorial Modal */}
      {showTutorialModal && (
        <TutorialModal user={user} onClose={() => setShowTutorialModal(false)} />
      )}

      {/* Trigger UT Pay confirmation */}
      <ConfirmModal
        open={showUtPayConfirm}
        title="Trigger UT Pay?"
        message="Every tenant wallet you manage will be checked, and any money in them will be used to pay their outstanding utility bills. This cannot be undone."
        icon="lightning-charge-fill"
        tone="teal"
        confirmLabel="Yes, trigger pay"
        busy={utBillsTriggerPayLoading}
        onConfirm={triggerManagerUtPay}
        onCancel={() => setShowUtPayConfirm(false)}
      />

      {/* Add Funds Modal (FRP Wallet) */}
      {
        showAddFundsForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Add Funds to FRP Wallet</h3>
                <button
                  onClick={() => setShowAddFundsForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="x" size="1.5rem" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Current FRP Wallet balance: {formatMoney(walletBalance)}
              </p>
              <form onSubmit={addFunds}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount to Add
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={addFundsAmount}
                    onChange={(e) => setAddFundsAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter amount"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Add Funds
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddFundsForm(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Add Funds Modal (Groups Wallet - testing) */}
      {
        showAddGroupWalletFundsForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Top Up Groups Wallet (Test)</h3>
                <button
                  onClick={() => setShowAddGroupWalletFundsForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="x" size="1.5rem" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-1">
                Current Groups Wallet balance: {formatMoney(groupWalletBalance)}
              </p>
              <p className="text-xs text-blue-700 mb-4">
                This is a testing top-up path and not connected to M-Pesa yet.
              </p>
              <form onSubmit={addGroupWalletFunds}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount to Add
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={addGroupWalletFundsAmount}
                    onChange={(e) => setAddGroupWalletFundsAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter amount"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Top Up
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddGroupWalletFundsForm(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Add Funds Modal (UT Bills Wallet - staging/testing) */}
      {
        showAddUtBillsFundsForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Top Up UT Bills Wallet</h3>
                <button
                  onClick={() => setShowAddUtBillsFundsForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="x" size="1.5rem" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-1">
                Current UT Bills Wallet balance: {formatMoney(utBillsWalletBalance)}
              </p>
              <p className="text-xs text-teal-700 mb-4">
                This top-up is available only on staging/dev.
              </p>
              <form onSubmit={addUtBillsFunds}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount to Add
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={addUtBillsFundsAmount}
                    onChange={(e) => setAddUtBillsFundsAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter amount"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="flex-1 bg-teal-600 text-white py-2 rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    Top Up
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddUtBillsFundsForm(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Management Company Modal */}
      {
        showManagementModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Create Management Company</h3>
                <button
                  onClick={() => {
                    setShowManagementModal(false)
                    setFormError('')
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="x" size="1.5rem" />
                </button>
              </div>

              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-red-700 text-sm">{formError}</p>
                </div>
              )}

              <form onSubmit={handleManagementSave}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    data-tutorial="mgmt-company-name"
                    value={managementForm.companyName}
                    onChange={(e) => setManagementForm({ ...managementForm, companyName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter company name"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telephone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    data-tutorial="mgmt-telephone"
                    value={managementForm.telephoneNumber}
                    onChange={(e) => setManagementForm({ ...managementForm, telephoneNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter phone number"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Head Office Location <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    data-tutorial="mgmt-office"
                    value={managementForm.headOfficeLocation}
                    onChange={(e) => setManagementForm({ ...managementForm, headOfficeLocation: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter head office address"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Position in Company <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    data-tutorial="mgmt-position"
                    value={managementForm.yourPositionInCompany}
                    onChange={(e) => setManagementForm({ ...managementForm, yourPositionInCompany: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., Manager, Director"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Owner Name(s) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    data-tutorial="mgmt-owner"
                    value={managementForm.ownerNames}
                    onChange={(e) => setManagementForm({ ...managementForm, ownerNames: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter owner name(s)"
                    required
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    data-tutorial="mgmt-submit"
                    disabled={formLoading}
                    className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {formLoading ? 'Creating...' : 'Create Management Company'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowManagementModal(false)
                      setFormError('')
                      setManagementForm({
                        companyName: '',
                        telephoneNumber: '',
                        headOfficeLocation: '',
                        yourPositionInCompany: '',
                        ownerNames: ''
                      })
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* FRP Wallet Transactions Modal */}
      {
        showWalletTransactionsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="bg-gradient-to-r from-green-600 to-green-700 p-4 sm:p-6 text-white flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-2">FRP Wallet Transactions</h3>
                    <div className="text-sm opacity-90">
                      <span className="font-medium">Current FRP Wallet Balance: {formatMoney(walletBalance)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowWalletTransactionsModal(false)
                      setWalletStartDate('')
                      setWalletEndDate('')
                    }}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Date Filter Section */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setWalletFilterOpen(prev => !prev)}
                  className="w-full flex items-center justify-between text-gray-800 font-medium"
                >
                  <span>Filter</span>
                  <Icon name={walletFilterOpen ? 'chevron-up' : 'chevron-down'} size="1rem" className="text-gray-600 sm:w-5 sm:h-5" />
                </button>
                {walletFilterOpen && (
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-4 sm:items-end">
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={walletStartDate}
                        onChange={(e) => setWalletStartDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={walletEndDate}
                        onChange={(e) => setWalletEndDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        onClick={fetchFilteredWalletTransactions}
                        className="flex-1 sm:flex-none bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                      >
                        Filter
                      </button>
                      <button
                        onClick={clearWalletDateFilters}
                        className="flex-1 sm:flex-none bg-gray-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="p-4 sm:p-6">
                  {walletTransactionsLoading ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Loading transactions...</p>
                    </div>
                  ) : walletTransactions.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500 text-sm">No transactions found.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-300 min-w-full">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-left text-xs sm:text-sm font-medium text-gray-700 w-24 sm:w-28">Date</th>
                            <th className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-left text-xs sm:text-sm font-medium text-gray-700 min-w-0">Description</th>
                            <th className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-left text-xs sm:text-sm font-medium text-gray-700 w-20 sm:w-24">Amount</th>
                            <th className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-left text-xs sm:text-sm font-medium text-gray-700 w-20 sm:w-24">New Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {walletTransactions.map((transaction, index) => {
                            const hasBalanceSnapshot = transaction?.balanceAfter !== null &&
                              transaction?.balanceAfter !== undefined &&
                              Number.isFinite(Number(transaction.balanceAfter));
                            let balanceAfterTransaction = hasBalanceSnapshot ? Number(transaction.balanceAfter) : walletBalance || 0;
                            if (!hasBalanceSnapshot) {
                              for (let i = 0; i < index; i++) {
                                balanceAfterTransaction -= walletTransactions[i].amount;
                              }
                            }

                            return (
                              <tr key={transaction._id || index} className="hover:bg-gray-50">
                                <td className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm whitespace-normal break-words">
                                  <div className="block sm:hidden break-inside-avoid">
                                    {formatDateWithTime(transaction.timestamp)}
                                  </div>
                                  <div className="hidden sm:block break-inside-avoid">
                                    {formatDateWithTime(transaction.timestamp)}
                                  </div>
                                </td>
                                <td className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm">
                                  <div className="break-words max-w-xs sm:max-w-md">
                                    {transaction.description}
                                  </div>
                                </td>
                                <td className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-medium whitespace-nowrap">
                                  {transaction.amount >= 0 ? (
                                    <span className="text-green-600">+{formatMoney(transaction.amount)}</span>
                                  ) : (
                                    <span className="text-red-600">{formatMoney(transaction.amount)}</span>
                                  )}
                                </td>
                                <td className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-medium whitespace-nowrap">
                                  {formatMoney(balanceAfterTransaction)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Groups Wallet Transactions Modal */}
      {
        showGroupWalletTransactionsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-6 text-white flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-2">Groups Wallet Transactions</h3>
                    <div className="text-sm opacity-90">
                      <span className="font-medium">Current Groups Wallet Balance: {formatMoney(groupWalletBalance)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowGroupWalletTransactionsModal(false)
                      setGroupWalletStartDate('')
                      setGroupWalletEndDate('')
                    }}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setGroupWalletFilterOpen(prev => !prev)}
                  className="w-full flex items-center justify-between text-gray-800 font-medium"
                >
                  <span>Filter</span>
                  <Icon name={groupWalletFilterOpen ? 'chevron-up' : 'chevron-down'} size="1rem" className="text-gray-600 sm:w-5 sm:h-5" />
                </button>
                {groupWalletFilterOpen && (
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-4 sm:items-end">
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={groupWalletStartDate}
                        onChange={(e) => setGroupWalletStartDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={groupWalletEndDate}
                        onChange={(e) => setGroupWalletEndDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        onClick={fetchFilteredGroupWalletTransactions}
                        className="flex-1 sm:flex-none bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                      >
                        Filter
                      </button>
                      <button
                        onClick={clearGroupWalletDateFilters}
                        className="flex-1 sm:flex-none bg-gray-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="p-4 sm:p-6">
                  {groupWalletTransactionsLoading ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Loading transactions...</p>
                    </div>
                  ) : groupWalletTransactions.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500 text-sm">No transactions found.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-300 min-w-full">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-left text-xs sm:text-sm font-medium text-gray-700 w-24 sm:w-28">Date</th>
                            <th className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-left text-xs sm:text-sm font-medium text-gray-700 min-w-0">Description</th>
                            <th className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-left text-xs sm:text-sm font-medium text-gray-700 w-20 sm:w-24">Amount</th>
                            <th className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-left text-xs sm:text-sm font-medium text-gray-700 w-20 sm:w-24">New Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupWalletTransactions.map((transaction, index) => {
                            const hasBalanceSnapshot = transaction?.balanceAfter !== null &&
                              transaction?.balanceAfter !== undefined &&
                              Number.isFinite(Number(transaction.balanceAfter));
                            let balanceAfterTransaction = hasBalanceSnapshot ? Number(transaction.balanceAfter) : groupWalletBalance || 0;
                            if (!hasBalanceSnapshot) {
                              for (let i = 0; i < index; i++) {
                                balanceAfterTransaction -= groupWalletTransactions[i].amount;
                              }
                            }

                            return (
                              <tr key={transaction._id || index} className="hover:bg-gray-50">
                                <td className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm whitespace-normal break-words">
                                  <div className="block sm:hidden break-inside-avoid">
                                    {formatDateWithTime(transaction.timestamp)}
                                  </div>
                                  <div className="hidden sm:block break-inside-avoid">
                                    {formatDateWithTime(transaction.timestamp)}
                                  </div>
                                </td>
                                <td className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm">
                                  <div className="break-words max-w-xs sm:max-w-md">
                                    {transaction.description}
                                  </div>
                                </td>
                                <td className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-medium whitespace-nowrap">
                                  {transaction.amount >= 0 ? (
                                    <span className="text-blue-600">+{formatMoney(transaction.amount)}</span>
                                  ) : (
                                    <span className="text-red-600">{formatMoney(transaction.amount)}</span>
                                  )}
                                </td>
                                <td className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-medium whitespace-nowrap">
                                  {formatMoney(balanceAfterTransaction)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      {/* UT Bills Statement Modal */}
      {
        showUtBillsTransactionsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="bg-gradient-to-r from-teal-600 to-emerald-700 p-4 sm:p-6 text-white flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-2">UT Bills Statement</h3>
                    <div className="text-sm opacity-90">
                      <span className="font-medium">Current UT Bills Wallet Balance: {formatMoney(utBillsWalletBalance)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowUtBillsTransactionsModal(false)}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                  >
                    <Icon name="x" size="1.2rem" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="p-4 sm:p-6">
                  {utBillsStatementLoading ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Loading statement...</p>
                    </div>
                  ) : utBillsStatementRows.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500 text-sm">No UT Bills wallet transactions found.</p>
                    </div>
                  ) : (
                    <div>
                      <table className="w-full table-fixed border-collapse border border-gray-300 text-[10px] leading-tight sm:text-sm">
                        <colgroup>
                          <col style={{ width: "18%" }} />
                          <col style={{ width: "38%" }} />
                          <col style={{ width: "14%" }} />
                          <col style={{ width: "14%" }} />
                          <col style={{ width: "16%" }} />
                        </colgroup>
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border border-gray-300 px-1 py-1 text-left font-medium text-gray-700 sm:px-4 sm:py-2">Date</th>
                            <th className="border border-gray-300 px-1 py-1 text-left font-medium text-gray-700 sm:px-4 sm:py-2">Description</th>
                            <th className="border border-gray-300 px-1 py-1 text-right font-medium text-gray-700 sm:px-4 sm:py-2">Amount In</th>
                            <th className="border border-gray-300 px-1 py-1 text-right font-medium text-gray-700 sm:px-4 sm:py-2">Amount Out</th>
                            <th className="border border-gray-300 px-1 py-1 text-right font-medium text-gray-700 sm:px-4 sm:py-2">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {utBillsStatementRows.map((row, index) => {
                            const amountIn = getWalletAmountIn(row)
                            const amountOut = getWalletAmountOut(row)
                            const balance = getWalletRunningBalance(row)
                            return (
                              <tr key={row._id || index} className="hover:bg-gray-50">
                                <td className="border border-gray-300 px-1 py-1 align-top break-words text-gray-700 sm:px-4 sm:py-2">
                                  {row.timestamp ? formatDateWithTime(row.timestamp) : "-"}
                                </td>
                                <td className="border border-gray-300 px-1 py-1 align-top break-words sm:px-4 sm:py-2">
                                  {row.description}
                                </td>
                                <td className="border border-gray-300 px-1 py-1 align-top break-words text-right font-medium text-emerald-700 sm:px-4 sm:py-2">
                                  {amountIn > 0 ? formatMoney(amountIn) : ""}
                                </td>
                                <td className="border border-gray-300 px-1 py-1 align-top break-words text-right font-medium text-red-600 sm:px-4 sm:py-2">
                                  {amountOut > 0 ? formatMoney(amountOut) : ""}
                                </td>
                                <td className="border border-gray-300 px-1 py-1 align-top break-words text-right font-semibold text-gray-800 sm:px-4 sm:py-2">
                                  {balance !== null ? formatMoney(balance) : ""}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
    </div >
  );
}

export default Dashboard
